import { useQueryClient } from '@tanstack/react-query';
import { CellContext } from '@tanstack/react-table';
import {
  Alert,
  AlertBody,
  BiodiversityIcon,
  Button,
  ButtonLink,
  cn,
  Co2Icon,
  Pill,
  Popover,
  PopoverAnchor,
  PopoverContent,
  RiErrorWarningFill,
  RiPencilFill,
  Stack,
  toast,
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipTrigger,
  WaterIcon,
} from 'component-library';
import { TextInput } from 'component-library/TextInput/TextInput';
import i18next from 'i18next';
import {
  FC,
  HTMLAttributes,
  HTMLProps,
  PropsWithChildren,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useResizeObserver } from 'usehooks-ts';

import { RestApiBadRequestServerError } from '@/api/rest/resources/errors/RestApiBadRequestError';
import { usePatchPlot } from '@/api/rest/resources/plot';
import { FactsByFactType } from '@/api/rest/resources/types/dashboard';
import { IndicatorName, PlotReportFactElement } from '@/api/rest/resources/types/fact';
import { MembershipWithOrganizationTypeEnum } from '@/api/rest/resources/types/membership';
import { DraftPlot, Plot, PlotStatusEnum, PlotType } from '@/api/rest/resources/types/plot';
import { Form, FormControl, FormField, FormLabel, FormMessage, PlotTypeIcon } from '@/components';
import { PlotMarkerThumbnail, PlotThumbnail } from '@/components/MapThumbnail/MapThumbnail';
import { getNCChangeIcon } from '@/components/NCFactCapsule/NCFactCapsule';
import { Icon } from '@/components/widgets/a/Icon/Icon';
import { NO_DATA_PLACEHOLDER, UNANALYZABLE_PLOT_STATUS_TYPES } from '@/config/constants';
import { useAboveGroundCarbonFacts } from '@/hooks/useAboveGroundCarbonFacts';
import { getDisplayNumber, useDisplayNumber } from '@/hooks/useDisplayNumber';
import { useMembershipType } from '@/hooks/useMembershipType';
import { useHiddenFactConfigurationByFactType } from '@/hooks/useWidgetConfigurationByOrganizationId';
import { Logger } from '@/lib/logs/logger';
import { buildPlotEditPath } from '@/pages/shared/plots/utils/buildPlotEditPath';
import { paths } from '@/routing';
import { isNullish } from '@/utils/booleans';
import { valueToTonne } from '@/utils/formatting';
import { printDDMMYYYYHHMM } from '@/utils/formatting/date';
import { squareMetersToHectares } from '@/utils/plot';
import {
  getColorOfPill,
  getColorOfPlotStatusNotice,
  getTextColorOfPill,
  getTextColorOfPlotStatusNotice,
} from '@/utils/plot/get-color-of-plot-status/getColorOfPlotStatus';

export type PlotRow = Plot & { facts: FactsByFactType };

const T_HA_UNIT = 'global.ui.unit.t/ha';

const maxDisplayCropCount = 3;

export const NameCell = ({ row }: CellContext<PlotRow, unknown>) => {
  const plot = row.original;
  const membershipType = useMembershipType();

  const { t } = useTranslation();

  const createdAt = printDDMMYYYYHHMM(plot.created_at) ?? '';
  const modifiedAt = printDDMMYYYYHHMM(plot.modified_at) ?? '';
  const analysedAt = printDDMMYYYYHHMM(plot.last_analyzed_at) ?? '';

  const editedAt =
    modifiedAt > createdAt
      ? t('shared.plots.tooltips.edited', { timestamp: modifiedAt })
      : t('shared.plots.tooltips.uploaded', { timestamp: createdAt });
  const lastActionHint =
    plot.status === PlotStatusEnum.analysed ? t('shared.plots.tooltips.analysed', { timestamp: analysedAt }) : editedAt;

  const [isRenamingPlot, setIsRenamingPlot] = useState(false);
  const canEditPlot = membershipType === MembershipWithOrganizationTypeEnum.land_steward;
  const isApproximate = plot.approximation_method === 'circle';

  let thumbnailComponent;
  if (isApproximate) {
    thumbnailComponent = <PlotMarkerThumbnail plot={plot} className='h-12 w-12 shrink-0 rounded-lg' />;
  } else if ('status' in plot && plot.status === PlotStatusEnum.invalid) {
    thumbnailComponent = (
      <span className='flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-bg-light-grey'>
        <RiErrorWarningFill size={20} className='text-error' data-testid='alert-icon' />
      </span>
    );
  } else {
    thumbnailComponent = <PlotThumbnail plot={plot} className='h-12 w-12 shrink-0 rounded-lg' />;
  }

  return (
    <Stack direction='row' centerMain spacing={3}>
      {thumbnailComponent}
      <Tooltip open={isRenamingPlot ? false : undefined}>
        <TooltipContent sideOffset={5} side='top'>
          {plot.name} ({lastActionHint})
          <TooltipArrow />
        </TooltipContent>
        <TooltipTrigger asChild>
          <Stack direction='col' spacing={2} className='col-start-1 row-start-1 w-[150px] items-start'>
            <Stack direction='row' spacing={1} className='w-full justify-between'>
              {canEditPlot && isRenamingPlot ? (
                <Popover defaultOpen modal onOpenChange={(open) => setIsRenamingPlot(open)}>
                  <PopoverAnchor asChild>
                    <span className='max-w-full truncate'>{plot.name}</span>
                  </PopoverAnchor>
                  <PopoverContent className='w-[320px]'>
                    <RenamePlot plot={plot as Plot} onDismiss={() => setIsRenamingPlot(false)} />
                  </PopoverContent>
                </Popover>
              ) : (
                <span className='max-w-full truncate'>{plot.name}</span>
              )}
              {canEditPlot && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsRenamingPlot(true);
                  }}
                  title={t('shared.plots.renamePlot.editPlotName')}
                  className={cn('invisible', !isRenamingPlot && 'group-hover/plot-row:visible')}
                >
                  <RiPencilFill />
                </button>
              )}
            </Stack>
          </Stack>
        </TooltipTrigger>
      </Tooltip>
    </Stack>
  );
};

export const LabelNameCell = ({ row }: CellContext<PlotRow, unknown>) => {
  const asset = row.original;

  return (
    <Stack direction='row' centerMain spacing={3}>
      {'status' in asset && asset.status === PlotStatusEnum.invalid ? (
        <span className='flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-bg-light-grey'>
          <RiErrorWarningFill size={20} className='text-error' data-testid='alert-icon' />
        </span>
      ) : (
        <PlotThumbnail plot={asset} className='h-12 w-12 shrink-0 rounded-lg' withInfo={false} />
      )}
      <Tooltip>
        <TooltipTrigger asChild>
          <span className='max-w-full truncate'>{asset.name}</span>
        </TooltipTrigger>
        <TooltipContent sideOffset={5} side='top'>
          {asset.name}
          <TooltipArrow />
        </TooltipContent>
      </Tooltip>
    </Stack>
  );
};

type RenamePlotProps = HTMLAttributes<HTMLFormElement> & { plot: Plot | DraftPlot; onDismiss: () => void };

export const RenamePlot = ({ plot, onDismiss, onClick, ...delegated }: RenamePlotProps) => {
  const membershipType = useMembershipType();
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const patchPlotMutation = usePatchPlot({ mutationKey: [membershipType, 'plot', plot.id] });

  type FormFields = { 'plot-name': string };
  const submit = async (formFields: FormFields) => {
    try {
      await patchPlotMutation.mutateAsync({
        pathVariables: { id: plot.id },
        bodyData: { name: formFields['plot-name'] },
      });

      await queryClient.invalidateQueries({ queryKey: [membershipType, 'plot', plot.id] });
      await queryClient.invalidateQueries({ queryKey: ['plots', 'organization'] });
      toast({
        title: t('shared.plots.renamePlot.success'),
        type: 'success',
      });
      onDismiss();
    } catch (error) {
      if (error instanceof RestApiBadRequestServerError && typeof error.errors[0] === 'string') {
        form.setError('plot-name', { type: 'custom', message: error.errors[0] });

        return;
      }

      toast({
        title: t('global.ui.toast.errorToastFallbackTitle'),
        description: t('global.ui.toast.errorToastFallbackDescription'),
        type: 'error',
      });
    }
  };

  const form = useForm<FormFields>({ defaultValues: { 'plot-name': plot.name } });

  return (
    <Form {...form}>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */}
      <form
        onSubmit={form.handleSubmit(submit)}
        onClick={(e) => {
          e.stopPropagation();
          onClick?.(e);
        }}
        {...delegated}
      >
        <Stack>
          <FormField
            name='plot-name'
            control={form.control}
            rules={{ required: { value: true, message: t('shared.plots.renamePlot.error.empty') } }}
            render={({ field, fieldState }) => (
              <>
                <FormLabel className='typography-button2 mb-2.5 text-text-secondary'>
                  {t('shared.plots.renamePlot.editPlotName')}
                </FormLabel>
                <FormControl>
                  <TextInput
                    /* eslint-disable-next-line jsx-a11y/no-autofocus */
                    autoFocus
                    data-testid='plot-name-text-input'
                    onFocus={(e) => {
                      e.target.select();
                    }}
                    className='bg-white-100 py-[1.5rem]'
                    {...field}
                  />
                </FormControl>
                <FormMessage>{fieldState.error?.message}</FormMessage>
              </>
            )}
          />
          <Stack direction='row' spacing={2} className='mt-3 justify-end'>
            <Button type='button' variant='text' size='small' onClick={onDismiss} data-testid='dismiss-button'>
              {t('global.ui.buttons.cancel')}
            </Button>
            <Button type='submit' variant='contained' size='small' data-testid='submit-button'>
              {t('global.ui.buttons.save')}
            </Button>
          </Stack>
        </Stack>
      </form>
    </Form>
  );
};

const FIXED_LABELS_HEIGHT_PX = 36;

export const LabelsCell = ({ row }: CellContext<PlotRow, unknown>) => {
  const { t } = useTranslation();
  const plot = row.original;

  const labels = plot.labels?.sort((a, b) => `${a.name}: ${a.value}`.length - `${b.name}: ${b.value}`.length) ?? [];
  const totalPillsCount = labels.length;

  const [visibleCount, setVisibleCount] = useState(labels?.length ?? 0);

  const visibleLabelsRef = useRef<HTMLDivElement>(null);
  const { width: containerWidth } = useResizeObserver({ ref: visibleLabelsRef });
  const prevContainerWidth = useRef(containerWidth);

  useLayoutEffect(() => {
    const container = visibleLabelsRef.current;
    if (!container) return;

    if (prevContainerWidth.current !== containerWidth) {
      prevContainerWidth.current = containerWidth;
      setVisibleCount(totalPillsCount);
      return;
    }

    if (visibleCount <= 0) return;
    if (container.scrollHeight > container.clientHeight) {
      setVisibleCount((current) => Math.max(0, current - 1));
    }
  }, [containerWidth, visibleCount, totalPillsCount]);

  const renderedPillsCount = visibleCount;
  const extraLabels = Math.max(0, totalPillsCount - renderedPillsCount);

  const visibleLabels = labels.slice(0, visibleCount);
  const allLabels = labels;

  if (labels.length === 0) {
    return <span className='text-text-primary'>{NO_DATA_PLACEHOLDER}</span>;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Stack className='flex gap-1 py-0 justify-center'>
          <div
            ref={visibleLabelsRef}
            className='max-w-xs overflow-hidden'
            style={{ height: `${FIXED_LABELS_HEIGHT_PX}px` }}
          >
            <Stack
              key={`plot-label-${plot.id}`}
              className='flex flex-wrap flex-row justify-start items-start gap-0 p-0'
            >
              {visibleLabels.map((label, index) => (
                <span
                  key={`visible-plot-label-${plot.id}-${index}`}
                  className='items-center w-fit bg-bg-light-grey typography-body2 rounded-lg text-text-secondary shrink-0 whitespace-nowrap px-1 py-1'
                >
                  {`${label.name}: ${label.value}`}
                </span>
              ))}
            </Stack>
          </div>
          {extraLabels > 0 && (
            <span className='items-center w-fit bg-bg-light-grey typography-body2 rounded-lg text-text-secondary shrink-0 whitespace-nowrap px-1 py-1'>
              {t('shared.plots.additionalPlotLabels', { count: extraLabels })}
            </span>
          )}
        </Stack>
      </TooltipTrigger>
      <TooltipContent side='top' align='start' key={`plot-labels-tooltip-${plot.id}`}>
        <Stack key={`plot-labels-tooltip-${plot.id}`} className='gap-2'>
          {allLabels.map((label, index) => (
            <span
              key={`all-plot-label-${plot.id}-${index}`}
              className='items-center w-fit bg-bg-light-grey typography-body2 rounded-lg text-text-secondary shrink-0 whitespace-nowrap px-1 py-1'
            >
              {`${label.name}: ${label.value}`}
            </span>
          ))}
        </Stack>
      </TooltipContent>
    </Tooltip>
  );
};

export const AreaCell = ({ row }: CellContext<PlotRow, unknown> | CellContext<DraftPlot, unknown>) => {
  const plot = row.original;
  const isValid = 'status' in plot ? plot.status !== PlotStatusEnum.invalid : true;

  const areaInHectares = getDisplayNumber(squareMetersToHectares(plot.area), window.navigator.language);

  return <span className='whitespace-nowrap'>{isValid ? `${areaInHectares} ha` : '--'}</span>;
};

export const PlotTypeCell = ({ row }: CellContext<PlotRow, unknown>) => {
  const plot = row.original;
  const { t } = useTranslation();

  const plotTypeHint = useMemo(() => {
    const hasCrops = plot.type === PlotType.CROPLAND && plot.crops && plot.crops.length > 0;
    const crops = plot.crops
      .slice(0, maxDisplayCropCount)
      .map((crop) => t(`global.crops.${crop}`))
      .sort()
      .join(', ');
    return hasCrops ? t('shared.plots.tooltips.cropland', { crops }) : t(`global.plotTypes.${plot.type}`);
  }, [t, plot.type, plot.crops]);
  return (
    <Tooltip>
      <TooltipTrigger>
        <PlotTypeIcon type={plot.type} size={24} />
      </TooltipTrigger>
      <TooltipContent sideOffset={5} side='top'>
        {plotTypeHint}
        <TooltipArrow />
      </TooltipContent>
    </Tooltip>
  );
};

export const BiodiversityZoneCell = ({ row }: CellContext<PlotRow, unknown>) => {
  const { t } = useTranslation();
  const plot = row.original;

  const biodiversityZone = plot.facts[IndicatorName.biodiversity_buffer_zone_pct] as PlotReportFactElement<number>;

  const isFactHidden = useHiddenFactConfigurationByFactType(IndicatorName.biodiversity_buffer_zone_pct);
  let formattedFact = '--';

  const value = useDisplayNumber(biodiversityZone?.value ?? 0);
  const unit = t('global.ui.unit.%');

  if (isFactHidden) return <>{formattedFact}</>;

  if (!isNullish(biodiversityZone?.value)) {
    formattedFact = unit ? `${value} ${unit}` : `${value}`;
  }

  if (UNANALYZABLE_PLOT_STATUS_TYPES.includes(plot.status)) {
    return <PlotNotice {...plot} />;
  }

  const isOutdatedValue = formattedFact !== '--' && plot.status !== PlotStatusEnum.analysed;

  return (
    <FactTooltip factDate={biodiversityZone?.measured_at}>
      <Pill
        data-testid='bio-cell'
        size='small'
        style={{
          backgroundColor: getColorOfPill(isOutdatedValue),
          color: getTextColorOfPill(plot.status),
        }}
        leftAdornment={<BiodiversityIcon size={12} color={getTextColorOfPill(plot.status)} />}
      >
        {formattedFact}
      </Pill>
    </FactTooltip>
  );
};

export const BiodiversityZoneTrendCell = ({ row }: CellContext<PlotRow, unknown>) => {
  const { t } = useTranslation();
  const plot = row.original;

  const biodiversityZone = plot.facts[
    IndicatorName.biodiversity_buffer_zone_pct_previous_1y_uplift_change
  ] as PlotReportFactElement<number>;

  let formattedFact = '--';

  const value = useDisplayNumber(biodiversityZone?.value ?? 0);
  const unit = t('global.ui.unit.%');

  if (!isNullish(biodiversityZone?.value)) {
    formattedFact = unit ? `${value} ${unit}` : `${value}`;
  }

  if (UNANALYZABLE_PLOT_STATUS_TYPES.includes(plot.status)) {
    return null;
  }

  const isOutdatedValue = formattedFact !== '--' && plot.status !== PlotStatusEnum.analysed;

  return (
    <FactTooltip factDate={biodiversityZone?.measured_at}>
      <Pill
        data-testid='bio-trend-cell'
        size='small'
        style={{
          backgroundColor: getColorOfPill(isOutdatedValue),
          color: getTextColorOfPill(plot.status),
        }}
        leftAdornment={getNCChangeIcon(biodiversityZone)}
      >
        {formattedFact}
      </Pill>
    </FactTooltip>
  );
};

export const CarbonStorageBgCell = ({ row }: CellContext<PlotRow, unknown>) => {
  const { t } = useTranslation();

  const plot = row.original;

  const carbonStorageBg = plot.facts[IndicatorName.permanent_soil_carbon_per_ha] as PlotReportFactElement<number>;

  const isFactHidden = useHiddenFactConfigurationByFactType(IndicatorName.permanent_soil_carbon_per_ha);
  let formattedFact = '--';

  const value = useDisplayNumber(valueToTonne(carbonStorageBg?.value, carbonStorageBg?.unit) ?? '');
  const unit = t(T_HA_UNIT);

  if (isFactHidden) return <>{formattedFact}</>;

  if (!isNullish(carbonStorageBg?.value)) {
    formattedFact = unit ? `${value} ${unit}` : `${value}`;
  }

  if (UNANALYZABLE_PLOT_STATUS_TYPES.includes(plot.status)) {
    return null;
  }
  const isOutdatedValue = formattedFact !== '--' && plot.status !== PlotStatusEnum.analysed;

  return (
    <FactTooltip factDate={carbonStorageBg?.measured_at}>
      <Pill
        data-testid='carbon-cell'
        size='small'
        style={{
          backgroundColor: getColorOfPill(isOutdatedValue),
          color: getTextColorOfPill(plot.status),
        }}
        leftAdornment={<Co2Icon size={12} color={getTextColorOfPill(plot.status)} />}
      >
        {formattedFact}
        <Tooltip>
          <TooltipTrigger>
            <Icon icon='ri-information-line' className='text-active-54 typography-body1' />
          </TooltipTrigger>
          <TooltipContent className='whitespace-pre-wrap'>
            {t('shared.plots.tableHeader.belowGroundCarbon')}
          </TooltipContent>
        </Tooltip>
      </Pill>
    </FactTooltip>
  );
};

export const CarbonStorageBgTrendCell = ({ row }: CellContext<PlotRow, unknown>) => {
  const { t } = useTranslation();
  const plot = row.original;

  const carbonStorageBg = plot.facts[
    IndicatorName.permanent_soil_carbon_previous_1y_uplift_per_ha
  ] as PlotReportFactElement<number>;

  let formattedFact = '--';

  const value = useDisplayNumber(valueToTonne(carbonStorageBg?.value, carbonStorageBg?.unit) ?? '');
  const unit = t(T_HA_UNIT);

  if (!isNullish(carbonStorageBg?.value)) {
    formattedFact = unit ? `${value} ${unit}` : `${value}`;
  }

  if (UNANALYZABLE_PLOT_STATUS_TYPES.includes(plot.status)) {
    return null;
  }
  const isOutdatedValue = formattedFact !== '--' && plot.status !== PlotStatusEnum.analysed;

  return (
    <FactTooltip factDate={carbonStorageBg?.measured_at}>
      <Pill
        data-testid='carbon-trend-cell'
        size='small'
        style={{
          backgroundColor: getColorOfPill(isOutdatedValue),
          color: getTextColorOfPill(plot.status),
        }}
        leftAdornment={getNCChangeIcon(carbonStorageBg)}
      >
        {formattedFact}
      </Pill>
    </FactTooltip>
  );
};

export const CarbonStorageAgTrendCell = ({ row }: CellContext<PlotRow, unknown>) => {
  const { t } = useTranslation();
  const plot = row.original;

  const facts = useAboveGroundCarbonFacts();
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const carbonStorageAg = plot.facts[facts!.perHa.oneYearPctChange ?? ''] as PlotReportFactElement<number>;

  let formattedFact = '--';

  const value = useDisplayNumber(valueToTonne(carbonStorageAg?.value, carbonStorageAg?.unit) ?? '');
  const unit = t(T_HA_UNIT);

  if (!isNullish(carbonStorageAg?.value)) {
    formattedFact = unit ? `${value} ${unit}` : `${value}`;
  }

  if (UNANALYZABLE_PLOT_STATUS_TYPES.includes(plot.status)) {
    return null;
  }
  const isOutdatedValue = formattedFact !== '--' && plot.status !== PlotStatusEnum.analysed;

  return (
    <FactTooltip factDate={carbonStorageAg?.measured_at}>
      <Pill
        data-testid='carbon-trend-cell'
        size='small'
        style={{
          backgroundColor: getColorOfPill(isOutdatedValue),
          color: getTextColorOfPill(plot.status),
        }}
        leftAdornment={getNCChangeIcon(carbonStorageAg)}
      >
        {formattedFact}
      </Pill>
    </FactTooltip>
  );
};

export const CarbonStorageAgCell = ({ row }: CellContext<PlotRow, unknown>) => {
  const { t } = useTranslation();

  const plot = row.original;
  const { status, facts } = plot;

  const factNames = useAboveGroundCarbonFacts();
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const carbonStorageAg = facts[factNames!.perHa.latestAnalysis ?? ''] as PlotReportFactElement<number>;

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const isFactHidden = useHiddenFactConfigurationByFactType(factNames!.perHa.latestAnalysis);
  let formattedFact = '--';

  const value = useDisplayNumber(valueToTonne(carbonStorageAg?.value, carbonStorageAg?.unit) ?? '');
  const unit = t(T_HA_UNIT);

  if (isFactHidden) return <>{formattedFact}</>;

  if (!isNullish(carbonStorageAg?.value)) {
    formattedFact = unit ? `${value} ${unit}` : `${value}`;
  }

  if (UNANALYZABLE_PLOT_STATUS_TYPES.includes(status)) {
    return null;
  }
  const isOutdatedValue = formattedFact !== '--' && status !== PlotStatusEnum.analysed;

  return (
    <FactTooltip factDate={carbonStorageAg?.measured_at}>
      <Pill
        data-testid='above-ground-carbon-cell'
        size='small'
        style={{
          backgroundColor: getColorOfPill(isOutdatedValue),
          color: getTextColorOfPill(status),
        }}
        leftAdornment={<Co2Icon size={12} color={getTextColorOfPill(status)} />}
      >
        {formattedFact}
        <Tooltip>
          <TooltipTrigger>
            <Icon icon='ri-information-line' className='text-active-54 typography-body1' />
          </TooltipTrigger>
          <TooltipContent className='whitespace-pre-wrap'>
            {t('shared.plots.tableHeader.aboveGroundCarbon')}
          </TooltipContent>
        </Tooltip>
      </Pill>
    </FactTooltip>
  );
};

export const WaterHoldingCapacityCell = ({ row }: CellContext<PlotRow, unknown>) => {
  const { t } = useTranslation();
  const plot = row.original;

  const waterHoldingCapacity = plot.facts[IndicatorName.water_fc_per_ha] as PlotReportFactElement<number>;

  const isFactHidden = useHiddenFactConfigurationByFactType(IndicatorName.water_fc_per_ha);
  let formattedFact = '--';

  const value = useDisplayNumber(valueToTonne(waterHoldingCapacity?.value, waterHoldingCapacity?.unit) ?? '');
  const unit = t('global.ui.unit.m^3/ha');

  if (isFactHidden) return <>{formattedFact}</>;

  if (!isNullish(waterHoldingCapacity?.value)) {
    formattedFact = unit ? `${value} ${unit}` : `${value}`;
  }
  if (UNANALYZABLE_PLOT_STATUS_TYPES.includes(plot.status)) {
    return null;
  }
  const isOutdatedValue = formattedFact !== '--' && plot.status !== PlotStatusEnum.analysed;

  return (
    <FactTooltip factDate={waterHoldingCapacity?.measured_at}>
      <Pill
        data-testid='whc-cell'
        size='small'
        style={{
          backgroundColor: getColorOfPill(isOutdatedValue),
          color: getTextColorOfPill(plot.status),
        }}
        leftAdornment={<WaterIcon size={12} color={getTextColorOfPill(plot.status)} />}
      >
        {formattedFact}
      </Pill>
    </FactTooltip>
  );
};

export const WaterHoldingCapacityTrendCell = ({ row }: CellContext<PlotRow, unknown>) => {
  const { t } = useTranslation();
  const plot = row.original;

  const waterHoldingCapacity = plot.facts[
    IndicatorName.water_fc_previous_1y_uplift_per_ha
  ] as PlotReportFactElement<number>;

  let formattedFact = '--';

  const value = useDisplayNumber(valueToTonne(waterHoldingCapacity?.value, waterHoldingCapacity?.unit) ?? '');
  const unit = t('global.ui.unit.m^3/ha');

  if (!isNullish(waterHoldingCapacity?.value)) {
    formattedFact = unit ? `${value} ${unit}` : `${value}`;
  }
  if (UNANALYZABLE_PLOT_STATUS_TYPES.includes(plot.status)) {
    return null;
  }
  const isOutdatedValue = formattedFact !== '--' && plot.status !== PlotStatusEnum.analysed;

  return (
    <FactTooltip factDate={waterHoldingCapacity?.measured_at}>
      <Pill
        data-testid='whc-trend-cell'
        size='small'
        style={{
          backgroundColor: getColorOfPill(isOutdatedValue),
          color: getTextColorOfPill(plot.status),
        }}
        leftAdornment={getNCChangeIcon(waterHoldingCapacity)}
      >
        {formattedFact}
      </Pill>
    </FactTooltip>
  );
};

type FactTooltipProps = PropsWithChildren & {
  factDate?: string | null;
};

export const FactTooltip: FC<FactTooltipProps> = ({ children, factDate }) => {
  const { t } = useTranslation();

  if (factDate) {
    return (
      <Tooltip>
        <TooltipTrigger>{children}</TooltipTrigger>
        <TooltipContent sideOffset={5} side='top'>
          {t('shared.nca.landDistributionDetails.referenceDate', {
            date: new Date(factDate).toLocaleDateString(i18next.language),
          })}
          <TooltipArrow />
        </TooltipContent>
      </Tooltip>
    );
  }

  return <>{children}</>;
};

export const PlotNotice = (plot: PlotRow) => {
  const { t } = useTranslation();
  const membershipType = useMembershipType();
  const { pathname } = useLocation();

  const plotStatus = plot.status;

  const noticeLabel = () => {
    const exhaustivenessCheck = (status: never) => {
      Logger.error(`No notices defined for plot status "${status}"`);
      return null;
    };

    switch (plotStatus) {
      case PlotStatusEnum.invalid:
        if (membershipType === MembershipWithOrganizationTypeEnum.land_steward) {
          return `${t('shared.plots.plotNotice.invalidPlot.info')} ${t(
            'shared.plots.plotNotice.invalidPlot.action.info',
          )}`;
        }
        return t('shared.plots.plotNotice.invalidPlot.info');
      case PlotStatusEnum.new_plot:
        return t('shared.plots.plotNotice.newPlot.info');
      case PlotStatusEnum.draft:
        return t('shared.plots.plotNotice.draftPlot.info');
      case PlotStatusEnum.analysed:
      case PlotStatusEnum.failed:
      case PlotStatusEnum.ready_to_analyse:
      case PlotStatusEnum.scheduled_for_analysis:
        return null;
      default:
        return exhaustivenessCheck(plotStatus);
    }
  };

  const actionLabel = () => {
    const exhaustivenessCheck = (status: never) => {
      Logger.error(`No actions defined for plot status "${status}"`);
      return null;
    };

    switch (plotStatus) {
      case PlotStatusEnum.invalid:
        return t('shared.plots.plotNotice.invalidPlot.action.label');
      case PlotStatusEnum.new_plot:
        return t('shared.plots.plotNotice.newPlot.action.label');
      case PlotStatusEnum.draft:
        return t('shared.plots.plotNotice.draftPlot.action.label');
      case PlotStatusEnum.analysed:
      case PlotStatusEnum.failed:
      case PlotStatusEnum.ready_to_analyse:
      case PlotStatusEnum.scheduled_for_analysis:
        return null;
      default:
        return exhaustivenessCheck(plotStatus);
    }
  };
  return (
    <Alert
      className='max-w-[600px] overflow-visible p-0'
      style={{
        backgroundColor: getColorOfPlotStatusNotice(plotStatus),
        color: getTextColorOfPlotStatusNotice(plotStatus),
      }}
      data-testid='plot-list-action-notice'
    >
      <AlertBody className='flex h-12 min-w-0 flex-row items-center justify-between gap-2'>
        <span className='flex-1 truncate'>{noticeLabel()}</span>
        {membershipType === MembershipWithOrganizationTypeEnum.land_steward && (
          <ButtonLink
            data-testid='plot-list-action-notice-cta'
            variant='text'
            className='typography-button2 px-2 py-2'
            style={{
              color: getTextColorOfPlotStatusNotice(plotStatus),
            }}
            state={{
              previousPath: pathname,
            }}
            onClick={(e) => e.stopPropagation()}
            to={buildPlotEditPath(plot, paths.landSteward)}
            preventScrollReset
          >
            {actionLabel()}
          </ButtonLink>
        )}
      </AlertBody>
    </Alert>
  );
};

type CheckboxCellProps = HTMLProps<HTMLInputElement> & { indeterminate: boolean };
export const CheckboxCell: FC<CheckboxCellProps> = ({ indeterminate, className = '', onClick, ...delegated }) => {
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (ref.current && typeof indeterminate === 'boolean') {
      ref.current.indeterminate = !delegated.checked && indeterminate;
    }
  }, [ref, indeterminate, delegated.checked, onClick]);

  return (
    <input
      type='checkbox'
      ref={ref}
      className={cn('w-6 cursor-pointer checked:accent-secondary-100 rounded-2xl', className)}
      style={{ width: '1.1rem', height: '1.1rem' }}
      onClick={(e) => {
        e.stopPropagation();
        onClick?.(e);
      }}
      {...delegated}
    />
  );
};
